<script>
import { onMount } from "svelte";
import {
    playerState,
    updateTime,
    updateDuration,
    updatePlayingStatus,
} from "@app/store/playerStore.svelte.js";
import { 
    initializeAudioScrub, cleanupAudioScrub
} from "@app/store/audioScrubStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { adrState } from "@app/store/adrStore.svelte.js";
import { styleState } from "@app/store/styleStore.svelte.js";
import { externalDataState } from "@app/store/externalDataStore.svelte.js";
import ClosedCaptionPreview from "./ClosedCaptionPreview.svelte";
import SubtitlePreview from "./SubtitlePreview.svelte";
import TranscriptPreview from "./TranscriptPreview.svelte";
import AdPreview from "./AdPreview.svelte";
import AdrPreview from "./AdrPreview.svelte";
import debounce from "just-debounce-it";
import tcLib from "@cclib/lib/timecode.js";
import "vidstack/player/styles/base.css";
import "vidstack/player/styles/plyr/theme.css";
import "vidstack/player";
import "vidstack/player/layouts/plyr";
import "vidstack/player/ui";
import * as m from '@app/paraglide/messages.js';

/* HLS */
import HLS from "hls.js";

let frameTimer, unsubscribe, currentTimeUnsubscribe;
const updateTcFrame = async function () {
    try {
        if (player) {
            updateTime(player.currentTime);
        }
    } catch (err) {
        console.log("unable to update current time in store.", err.message);
    }
};

function handleComponentError(error) {
    console.error("Component Error:", error);
}

const goToMidi = debounce(
    (currentTime) => {
        let intValue = (currentTime * (adrState.bpm / 60)) / 0.25;
        let binaryValue = intValue.toString(2).padStart(14, "0");
        let binA = binaryValue.slice(-7);
        let binB = binaryValue.slice(0, 7);
        let spp = [0xf2, parseInt(binA, 2), parseInt(binB, 2)];
        let tc = tcLib.secToTc(
            currentTime,
            projectState.frameRate,
            projectState.dropFrame,
        );
        let tcArray = tc.split(/:|;/g);
        let midiCmd = [
            0xf0,
            0x7f,
            0x00,
            0x06,
            0x44,
            0x06,
            0x01,
            ...tcArray,
            0x00,
            0xf7,
        ];

        //console.log(`MIDI SEND: ${midiCmd} (${tc})`);
        adrState.selectedOutput.send(midiCmd);
        /* Go to Song Position */
        adrState.selectedOutput.send(spp);
    },
    500,
    true,
);

onMount(async () => {
    window.player = document.querySelector("media-player");
    window.player.poster = "./assets/img/cc-logo-bg.png";
    window.player.viewType = "video";
    window.player.playsInline = true;
    window.player.streamType = "on-demand";
    window.player.hideControlsOnMouseLeave = false;

    const plyrLayout = document.querySelector("media-plyr-layout");
    plyrLayout.controls = ["progress", "current-time", "fullscreen"];
    plyrLayout.seekTime = 1;
    plyrLayout.clickToPlay = false;
    plyrLayout.clickToFullscreen = false;

    player.addEventListener("duration-change", (ev) => {
        updateDuration(ev.detail);
    });

    player.addEventListener("seeked", (ev) => {
        updateTime(ev.detail);
    });

    player.addEventListener("seeking", (ev) => {
        // console.log("Seeking to", ev.detail);
        if (player.state.ended ) {
            ev.preventDefault();
            player?.$state.ended.set(false);
        } 
        
        updateTime(ev.detail);
    });

    player.addEventListener("error", (ev) => {
        console.log("Media Player Error:", ev);
    });

    player.addEventListener("play-fail", (event) => {
        console.log("Play failed:", event.detail);
    });

    player.addEventListener("provider-change", (event) => {
        const provider = event.detail;
        if (provider?.type === "hls") {
            // Static import
            console.log("HLS provider detected");
            provider.library = HLS;
            if (externalDataState.idToken && externalDataState.slug) {
                provider.config = {
                    xhrSetup(xhr){
                        xhr.setRequestHeader('Authorization', externalDataState.idToken);
                        xhr.setRequestHeader('x-organization-slug', externalDataState.slug);
                    }
                };
            }
        }
    });

    player.addEventListener("source-change", (event) => {
        const source = event.detail;
        console.log("Source changed to", source);
        player.playbackRate = playerState.speed;
        try{
            console.log("Cleaning up audio scrub before source change");
            cleanupAudioScrub();
        } catch(err) {
            console.log("Error cleaning up audio scrub:", err.message);
        }
        
        setTimeout(() => {
            updatePlayingStatus(false);  
            if (projectState?.media?.storage === "Local Storage"){
                console.log("Initializing audio scrub for local storage");
                initializeAudioScrub();
            }                      
        }, 1500);
    });

    player.addEventListener("onHlsErrror", (ev) => {
        console.log("HLS Error:", ev);
    });

    unsubscribe = player.subscribe(({ playing }) => {
        updatePlayingStatus(playing);       

        if (playing) {
            //console.log("PLAY");
            if (frameTimer) {
                clearInterval(frameTimer);
            }

            frameTimer = setInterval(updateTcFrame, 50);

            if (adrState.enable && adrState.selectedOutput) {
                if (adrState.record) {
                    /* Record Command */
                    //console.log(`MIDI SEND: RECORD`);
                    adrState.selectedOutput.send([
                        0xf0, 0x7f, 0x00, 0x06, 0x06, 0xf7,
                    ]);
                    adrState.selectedOutput.send([251]);
                } else {
                    /* Playback command */
                    //console.log(`MIDI SEND: PLAY`);
                    adrState.selectedOutput.send([
                        0xf0, 0x7f, 0x00, 0x06, 0x02, 0xf7,
                    ]);
                    adrState.selectedOutput.send([251]);
                }
            }
        } else {
            if (frameTimer) {
                clearInterval(frameTimer);
            }

            if (adrState.enable && adrState.selectedOutput) {
                /* Pause Command */
                //console.log(`MIDI SEND: PAUSE`);
                adrState.selectedOutput.send([
                    0xf0, 0x7f, 0x00, 0x06, 0x01, 0xf7,
                ]);
                adrState.selectedOutput.send([252]);
            }
        }
    });

    currentTimeUnsubscribe = player.subscribe(({ currentTime }) => {
        // console.log("Time Change", currentTime);
        updateTime(currentTime);
        if (adrState.enable && adrState.selectedOutput) {
            goToMidi(ev.detail);
        }
    });

    try {
        player.volume = 1;
        player.playbackRate = 1;
    } catch (err) {
        console.log(
            "Error setting player volume or playback rate",
            err.message,
        );
    }

    return () => {
        console.log("Destroying Media Player");
        player.destroy();
        if (frameTimer) {
            clearInterval(frameTimer);
        }

        if (unsubscribe) {
            unsubscribe();
        }

        if (currentTimeUnsubscribe) {
            currentTimeUnsubscribe();
        }

        cleanupAudioScrub();
    };
});
</script>

<media-player
    playsinline
    keep-alive
    aria-label={m.player_video_player_aria_label()}
    class="plyr"
    style="aspect-ratio: {projectState?.media?.aspectRatio || "16/9"}; background-color:black">
    <media-provider>
        {#if eventGroupState[projectState.selected] && eventGroupState[projectState.selected].type === "audio description" && styleState.adrPreview}
            <svelte:boundary onerror={handleComponentError}>
                <AdrPreview></AdrPreview>
            </svelte:boundary>
        {:else if eventGroupState[projectState.selected] && eventGroupState[projectState.selected].type === "audio description"}
            <svelte:boundary onerror={handleComponentError}>
                <AdPreview></AdPreview>
            </svelte:boundary>
        {:else if eventGroupState[projectState.selected] && eventGroupState[projectState.selected].type === "transcription"}
            <svelte:boundary onerror={handleComponentError}>
                <TranscriptPreview></TranscriptPreview>
            </svelte:boundary>
        {:else if eventGroupState[projectState.selected] && styleState.ccMode}
            <svelte:boundary onerror={handleComponentError}>
                <ClosedCaptionPreview></ClosedCaptionPreview>
            </svelte:boundary>
        {:else if eventGroupState[projectState.selected]}
            <svelte:boundary onerror={handleComponentError}>
                <SubtitlePreview></SubtitlePreview>
            </svelte:boundary>
        {/if}
    </media-provider>
    <media-plyr-layout></media-plyr-layout>
</media-player>

<style>
.plyr {
    --plyr-color-main: #0fbc8c;
    --plyr-border-radius: 0;
}
</style>
